import { EyeOutlined } from '@ant-design/icons'
import { PageHeader } from '@ant-design/pro-layout'
import { AdminCollapse } from '@salescore/client-common'
import {
  type CompiledFormViewConfig,
  type CompiledKpiPivotViewConfig,
  type CompiledKpiViewConfig,
  type CompiledSheetViewConfig,
  type ViewConfigTreeNode,
  viewConfigTreeNodeSchema,
  type ViewQueryList,
} from '@salescore/core'
import { App, Button, Collapse, Descriptions, type ModalFuncProps, Space, Typography } from 'antd'
import { t } from 'i18next'
import { useEffect } from 'react'
import { useRecoilValue } from 'recoil'

import { changesAtom } from '../../../../recoil/recordsHistory'
import { useGetModel } from '../../../../recoil/view/selectors/connectionsSelector'
import {
  useEditNodeMutation,
  useGetConfigNode,
  useSetNodeMutation,
  useViewConfigSheet,
} from '../../../../recoil/viewConfig'
import { buildViewDebugModalProperties } from '../ViewDebugModal.tsx'
import { FieldDefaultValuesForm } from './node/FieldDefaultValuesForm'

export const buildViewQueryNodeDetailModalProperties = ({
  nodeName,
  onAfterFinish,
  compiledConfig,
  query,
  setHasChangeToViewSchema,
}: {
  nodeName: string
  onAfterFinish: () => void
  compiledConfig: CompiledSheetViewConfig | CompiledKpiViewConfig | CompiledKpiPivotViewConfig | CompiledFormViewConfig
  query: ViewQueryList
  setHasChangeToViewSchema: (hasChangeToViewSchema: boolean) => void
}): ModalFuncProps => ({
  icon: null,
  title: null,
  width: '90%',
  className: 'max-w-[1000px]',
  closable: true,
  maskClosable: true,
  destroyOnClose: true,
  footer: null,
  content: (
    <ModalContent
      nodeName={nodeName}
      onAfterFinish={onAfterFinish}
      compiledConfig={compiledConfig}
      query={query}
      setHasChangeToViewSchema={setHasChangeToViewSchema}
    />
  ),
})

const ModalContent = ({
  nodeName,
  onAfterFinish,
  compiledConfig,
  query,
  setHasChangeToViewSchema,
}: {
  nodeName: string
  onAfterFinish: () => void
  compiledConfig: CompiledSheetViewConfig | CompiledKpiViewConfig | CompiledKpiPivotViewConfig | CompiledFormViewConfig
  query: ViewQueryList
  setHasChangeToViewSchema: (hasChangeToViewSchema: boolean) => void
}) => {
  const { modal } = App.useApp()
  const getModel = useGetModel()
  const config = useViewConfigSheet()
  const { editNode, setNode } = {
    editNode: useEditNodeMutation(),
    setNode: useSetNodeMutation(),
  }
  const changes = useRecoilValue(changesAtom)
  const { message } = App.useApp()
  const getConfigNode = useGetConfigNode(config)
  const node = getConfigNode(nodeName)

  useEffect(() => {
    if (changes.length > 0) {
      message.warning(t('設定を変更すると変更情報が失われます。'))
    }
  }, [changes.length, message])

  if (node === undefined) {
    onAfterFinish()
    return
  }
  const label = node.ui?.label ?? getModel(node.modelName)?.label ?? node.modelName

  return (
    <PageHeader title={t(`{{label}}のブロック設定`, { label })}>
      {/* eslint-disable-next-line better-tailwindcss/no-unknown-classes -- description-vertical-small-label は antd.less にスタイル定義あり */}
      <Descriptions layout="vertical" column={2} className="description-vertical-small-label">
        <Descriptions.Item label={t(`表示名`)}>
          <Typography.Paragraph
            editable={{
              onChange: (label) => {
                editNode({
                  node,
                  f: (oldNode) => {
                    // eslint-disable-next-line no-param-reassign
                    oldNode.ui = {
                      ...oldNode.ui,
                      label,
                    }
                  },
                  onSetConfig: () => {
                    setHasChangeToViewSchema(true)
                  },
                })
              },
            }}
          >
            {label}
          </Typography.Paragraph>
        </Descriptions.Item>
        <Descriptions.Item label={t(`テーブル名`)}>
          <Typography.Text code>{node.modelName}</Typography.Text>
        </Descriptions.Item>
      </Descriptions>
      <Collapse defaultActiveKey={[]} className="mt-4">
        <Collapse.Panel
          header={
            <Space>
              <span>{t(`デフォルト値設定`)}</span>
            </Space>
          }
          key="defaultValue"
        >
          <FieldDefaultValuesForm
            node={node}
            onAfterFinish={() => {
              onAfterFinish()
            }}
            compiledConfig={compiledConfig}
            query={query}
            setHasChangeToViewSchema={setHasChangeToViewSchema}
          />
        </Collapse.Panel>
      </Collapse>
      <AdminCollapse>
        <Space>
          <Button
            onClick={() => {
              const m = modal.confirm(
                buildViewDebugModalProperties({
                  content: node,
                  schema: viewConfigTreeNodeSchema,
                  onChange(newNode) {
                    setNode({
                      // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion
                      node: newNode as ViewConfigTreeNode,
                      onSetConfig: () => {
                        setHasChangeToViewSchema(true)
                      },
                    })
                    onAfterFinish()
                  },
                  onAfterFinish: () => {
                    m.destroy()
                  },
                }),
              )
            }}
            icon={<EyeOutlined />}
          >
            {t(`設定編集`)}
          </Button>
        </Space>
      </AdminCollapse>
    </PageHeader>
  )
}
